import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlayIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ClockIcon,
  DocumentTextIcon,
  SpeakerWaveIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Progress from '@/components/ui/Progress';
import Spinner from '@/components/ui/Spinner';
import { API_URL, ttsJobApi, getErrorMessage } from '@/lib/api';
import { JobControlButtons, PauseStatusIndicator, CheckpointPreview } from '@/components/tts';
import { ToastContainer, type ToastProps } from '@/components/ui/Toast';
import { getDisplayTitleFromPdfPath, type TTSJobStatus, type CheckpointId } from '@/types/api';

interface JobData {
  id?: number;
  job_id: string;
  status: TTSJobStatus;
  progress?: number;
  status_message?: string;
  created_at?: string | number;
  updated_at?: string | number;
  finished_at?: string | number;
  error_message?: string | null;
  metadata?: string;
  voice_id?: string;
  pdf_path?: string;
  tenant_id?: string;
  // Pause/Stop fields
  pause_requested?: boolean;
  stop_requested?: boolean;
  paused_at_checkpoint?: CheckpointId | null;
  paused_at?: string | null;
  stopped_at?: string | null;
  stop_reason?: string | null;
}

interface ParsedMetadata {
  job_id?: string;
  pdf_path?: string;
  tenant_id?: string;
  voice_id?: string;
  chunk_size?: number;
  quality?: string;
  skip_credits?: boolean;
  dry_run?: boolean;
  isbn?: string;
}

// Matches the actual API response from /jobs/:jobId/status
interface StepDetail {
  started: boolean;
  completed: boolean;
  startTime: string | null;
  endTime: string | null;
}

interface JobStatusResponse {
  success: boolean;
  job_id: string;
  status: string;
  last_event: string | null;
  current_step: string | null;
  last_completed_step: string | null;
  next_step: string | null;
  is_stuck: boolean;
  stuck_reason: string | null;
  events_count: number;
  last_activity: string | null;
  suggested_retry_step: string | null;
  pipeline_progress: {
    completed_steps: number;
    total_steps: number;
    percentage: number;
    step_details: Record<string, StepDetail>;
  };
  timestamp: string;
}

const ACTIVE_TTS_JOB_STATUSES = new Set<TTSJobStatus>(['pending', 'running', 'processing']);

// Pipeline steps mapping - keys match the API response
const PIPELINE_STEPS = [
  { key: 'pdf_ingestion', name: 'PDF Ingestion' },
  { key: 'image_processing', name: 'Image Processing' },
  { key: 'extract_structure', name: 'Structure Extraction' },
  { key: 'synthesize_tts', name: 'TTS Synthesis' },
  { key: 'assemble_audio', name: 'Audio Assembly' },
  { key: 'generate_credits', name: 'Credits Generation' },
  { key: 'qa_finalize', name: 'QA Finalize' },
];

function getStatusBadge(status: string, pauseRequested?: boolean) {
  switch (status?.toLowerCase()) {
    case 'completed':
      return <Badge variant="success" size="lg">Completed</Badge>;
    case 'processing':
    case 'running':
      if (pauseRequested) {
        return <Badge variant="warning" size="lg">Pausing...</Badge>;
      }
      return <Badge variant="primary" size="lg">Processing</Badge>;
    case 'pending':
      return <Badge variant="warning" size="lg">Pending</Badge>;
    case 'paused':
      return <Badge variant="warning" size="lg">Paused</Badge>;
    case 'stopped':
      return <Badge variant="slate" size="lg">Stopped</Badge>;
    case 'failed':
      return <Badge variant="danger" size="lg">Failed</Badge>;
    default:
      return <Badge variant="slate" size="lg">{status || 'Unknown'}</Badge>;
  }
}

function getStepIcon(step: StepDetail | undefined): string {
  if (!step) return '⏳';
  if (step.completed) return '✅';
  if (step.started) return '🔄';
  return '⏳';
}

function getStepStatus(step: StepDetail | undefined): 'completed' | 'in_progress' | 'pending' {
  if (!step) return 'pending';
  if (step.completed) return 'completed';
  if (step.started) return 'in_progress';
  return 'pending';
}

function calculateDuration(startTime: string | null, endTime: string | null): number | null {
  if (!startTime || !endTime) return null;
  try {
    return new Date(endTime).getTime() - new Date(startTime).getTime();
  } catch {
    return null;
  }
}

function formatDuration(ms: number | null): string {
  if (!ms) return '--';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function formatDate(dateInput: string | number | null | undefined): string {
  if (!dateInput) return '--';
  try {
    // Handle Unix timestamp (number or string that looks like a number)
    if (typeof dateInput === 'number' || /^\d+(\.\d+)?$/.test(String(dateInput))) {
      const timestamp = typeof dateInput === 'number' ? dateInput : parseFloat(String(dateInput));
      // If timestamp is in seconds (< year 2100 in ms), convert to ms
      const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
      return new Date(ms).toLocaleString();
    }
    return new Date(dateInput).toLocaleString();
  } catch {
    return String(dateInput);
  }
}

function minutesSince(dateStr: string | null): number | null {
  if (!dateStr) return null;
  try {
    const ms = Date.now() - new Date(dateStr).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    return Math.floor(ms / 60000);
  } catch {
    return null;
  }
}

function parseMetadata(metadataStr: string | undefined): ParsedMetadata {
  if (!metadataStr) return {};
  try {
    return JSON.parse(metadataStr);
  } catch {
    return {};
  }
}

export default function TTSJobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobData | null>(null);
  const [statusData, setStatusData] = useState<JobStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);

  // Helper to add a toast notification
  const addToast = useCallback((title: string, type: ToastProps['type'], message?: string) => {
    const id = `toast-${Date.now()}`;
    setToasts(prev => [...prev, { id, type, title, message, onClose: removeToast }]);
  }, []);

  // Helper to remove a toast notification
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Handler for pause action
  const handlePause = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await ttsJobApi.pause(jobId);
      addToast('Pause Requested', 'success', response.message || 'Job will pause at next checkpoint');
      // Update local state optimistically
      setJob(prev => prev ? { ...prev, pause_requested: true } : null);
    } catch (err) {
      addToast('Pause Failed', 'error', getErrorMessage(err));
    }
  }, [jobId, addToast]);

  // Handler for resume action
  const handleResume = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await ttsJobApi.resume(jobId);
      addToast('Job Resumed', 'success', response.message || 'Processing has resumed');
      // Update local state optimistically
      setJob(prev => prev ? { 
        ...prev, 
        status: 'processing' as TTSJobStatus, 
        pause_requested: false, 
        paused_at_checkpoint: null,
        paused_at: null 
      } : null);
    } catch (err) {
      addToast('Resume Failed', 'error', getErrorMessage(err));
    }
  }, [jobId, addToast]);

  // Handler for stop action
  const handleStop = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await ttsJobApi.stop(jobId, 'User requested stop');
      addToast('Job Stopped', 'success', response.message || 'Job has been permanently stopped');
      // Update local state optimistically
      setJob(prev => prev ? { 
        ...prev, 
        status: 'stopped' as TTSJobStatus, 
        stopped_at: new Date().toISOString() 
      } : null);
    } catch (err) {
      addToast('Stop Failed', 'error', getErrorMessage(err));
    }
  }, [jobId, addToast]);

  // Handler for delete action
  const handleDelete = useCallback(async () => {
    if (!jobId) return;
    try {
      await ttsJobApi.delete(jobId, true);
      addToast('Job Deleted', 'success', 'Job and files have been removed');
      // Navigate back to jobs list after a short delay
      setTimeout(() => navigate('/tts/jobs'), 1500);
    } catch (err) {
      addToast('Delete Failed', 'error', getErrorMessage(err));
    }
  }, [jobId, navigate, addToast]);

  // Handler for restart action (retry from scratch)
  const [isRestarting, setIsRestarting] = useState(false);
  const handleRestart = useCallback(async () => {
    if (!jobId || isRestarting) return;
    
    // Confirm with user
    if (!window.confirm('Are you sure you want to restart this job? This will delete all existing artifacts and start processing from scratch.')) {
      return;
    }
    
    setIsRestarting(true);
    try {
      const response = await ttsJobApi.restart(jobId);
      addToast('Job Restarting', 'success', `Job is being reprocessed from scratch (restart #${response.restart_count})`);
      // Update local state to show pending/processing
      setJob(prev => prev ? { 
        ...prev, 
        status: 'processing' as TTSJobStatus, 
        progress: 0,
        status_message: 'Job restarted - processing',
        error_message: null,
        pause_requested: false,
        paused_at_checkpoint: null,
        paused_at: null,
        stopped_at: null,
        stop_reason: null
      } : null);
    } catch (err) {
      addToast('Restart Failed', 'error', getErrorMessage(err));
    } finally {
      setIsRestarting(false);
    }
  }, [jobId, isRestarting, addToast]);

  const handleDownloadZip = useCallback(async () => {
    if (!job?.job_id || isDownloadingZip) return;

    setIsDownloadingZip(true);
    try {
      const token = localStorage.getItem('auth-token');
      const response = await fetch(`${API_URL}/tts/qa/${job.job_id}/zip`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        let message = `Download failed (${response.status})`;
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await response.json() as { error?: string; message?: string };
            message = data.error || data.message || message;
          } else {
            const text = await response.text();
            message = text || message;
          }
        } catch {
          // ignore parse errors
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `qa_${job.job_id}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
      addToast('Download Started', 'success', 'QA ZIP download has started');
    } catch (err) {
      addToast('Download Failed', 'error', getErrorMessage(err));
    } finally {
      setIsDownloadingZip(false);
    }
  }, [job?.job_id, isDownloadingZip, addToast]);

  useEffect(() => {
    if (!jobId) return;

    const fetchJobData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch basic job info and status in parallel
        const [jobResponse, statusResponse] = await Promise.all([
          fetch(`${API_URL}/tts/jobs/${jobId}`),
          fetch(`${API_URL}/tts/jobs/${jobId}/status`).catch(() => null),
        ]);

        if (!jobResponse.ok) {
          throw new Error(`Failed to fetch job: ${jobResponse.status}`);
        }

        const jobData = await jobResponse.json() as JobData;
        let nextJob = jobData;

        // Try to get status data (has pipeline progress)
        if (statusResponse?.ok) {
          const data = await statusResponse.json() as JobStatusResponse;
          setStatusData(data);
          nextJob = {
            ...jobData,
            status: (data.status as TTSJobStatus) || jobData.status,
            status_message: data.current_step || jobData.status_message,
            progress: Math.max(
              Number(jobData.progress ?? 0),
              Number(data.pipeline_progress?.percentage ?? 0)
            ),
          };
        }

        setJob(nextJob);
      } catch (err) {
        console.error('Error fetching job:', err);
        setError(err instanceof Error ? err.message : 'Failed to load job data');
      } finally {
        setLoading(false);
      }
    };

    fetchJobData();

    // Poll for updates if job is not completed
    const interval = setInterval(async () => {
      if (job?.status === 'completed' || job?.status === 'failed') {
        clearInterval(interval);
        return;
      }

      try {
        const [jobResponse, statusResponse] = await Promise.all([
          fetch(`${API_URL}/tts/jobs/${jobId}`),
          fetch(`${API_URL}/tts/jobs/${jobId}/status`).catch(() => null),
        ]);

        if (jobResponse.ok) {
          const jobData = await jobResponse.json() as JobData;
          let nextJob = jobData;

          if (statusResponse?.ok) {
            const data = await statusResponse.json() as JobStatusResponse;
            setStatusData(data);
            nextJob = {
              ...jobData,
              status: (data.status as TTSJobStatus) || jobData.status,
              status_message: data.current_step || jobData.status_message,
              progress: Math.max(
                Number(jobData.progress ?? 0),
                Number(data.pipeline_progress?.percentage ?? 0)
              ),
            };
          } else {
            setStatusData(null);
          }

          setJob(nextJob);
        }
      } catch (err) {
        console.error('Error polling job:', err);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [jobId, job?.status]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="space-y-6">
        <Link
          to="/tts/jobs"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Jobs
        </Link>
        <Card>
          <CardBody className="text-center py-12">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Error Loading Job</h2>
            <p className="text-slate-500">{error || 'Job not found'}</p>
            <Button
              variant="primary"
              className="mt-4"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const metadata = parseMetadata(job.metadata);
  const rawFileName = metadata.pdf_path?.split('/').pop() || job.pdf_path?.split('/').pop();
  const fileName = (() => {
    if (!rawFileName) return 'Unknown file';
    try {
      return decodeURIComponent(rawFileName);
    } catch {
      return rawFileName;
    }
  })();
  const title =
    getDisplayTitleFromPdfPath(metadata.pdf_path) ||
    getDisplayTitleFromPdfPath(job.pdf_path) ||
    'Unknown file';
  const effectiveStatus = (statusData?.status as TTSJobStatus | undefined) || job.status;

  // Get step details from status response
  const stepDetails = statusData?.pipeline_progress?.step_details || {};
  const pipelineProgress = statusData?.pipeline_progress;

  /**
   * "Stuck" is often a false positive for large PDFs. We gate the warning behind a
   * longer inactivity threshold and use softer language.
   */
  const STUCK_WARNING_AFTER_MINUTES = 240; // 4 hours
  const lastActivityMinutes = minutesSince(statusData?.last_activity || null);
  const showSlowProcessingWarning =
    Boolean(statusData?.is_stuck) &&
    job.status !== 'completed' &&
    job.status !== 'failed' &&
    lastActivityMinutes !== null &&
    lastActivityMinutes >= STUCK_WARNING_AFTER_MINUTES;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link
        to="/tts/jobs"
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Jobs
      </Link>

      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-slate-800 capitalize">{title}</h1>
            {getStatusBadge(job.status, job.pause_requested)}
          </div>
          <p className="text-slate-500 font-mono">{job.job_id}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {job.status === 'completed' && (
            <>
              <Link to={`/tts/player/${job.job_id}`}>
                <Button leftIcon={<PlayIcon className="w-4 h-4" />}>
                  Play Audio
                </Button>
              </Link>
              <Button
                variant="outline"
                leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
                onClick={handleDownloadZip}
                disabled={isDownloadingZip}
              >
                {isDownloadingZip ? 'Downloading...' : 'Download'}
              </Button>
            </>
          )}
          {(job.status === 'failed' || job.status === 'stopped') && (
            <Button 
              variant="secondary" 
              leftIcon={<ArrowPathIcon className="w-4 h-4" />}
              onClick={handleRestart}
              disabled={isRestarting}
            >
              {isRestarting ? 'Restarting...' : 'Retry Job'}
            </Button>
          )}
          <Link to={`/tts/jobs/${job.job_id}/monitor`}>
            <Button variant="outline" leftIcon={<ChartBarIcon className="w-4 h-4" />}>
              View Monitor
            </Button>
          </Link>
        </div>
      </div>

      {/* Job Controls Card - Pause/Resume/Stop */}
      <Card>
        <CardHeader title="Job Controls" />
        <CardBody className="space-y-4">
          {/* Pause Status Indicator */}
          <PauseStatusIndicator
            status={job.status}
            pausedAtCheckpoint={job.paused_at_checkpoint}
            pausedAt={job.paused_at}
            stoppedAt={job.stopped_at}
            stopReason={job.stop_reason}
          />

          {/* Control Buttons */}
          <JobControlButtons
            jobId={job.job_id}
            status={job.status}
            currentStep={statusData?.current_step || undefined}
            pauseRequested={job.pause_requested}
            onPause={handlePause}
            onResume={handleResume}
            onStop={handleStop}
            onDelete={job.status === 'stopped' ? handleDelete : undefined}
          />

          {/* Checkpoint Preview - show upcoming pause points */}
          {ACTIVE_TTS_JOB_STATUSES.has(effectiveStatus) && (
            <CheckpointPreview
              currentStep={statusData?.current_step}
              pauseRequested={job.pause_requested}
              completedSteps={
                statusData?.pipeline_progress?.step_details
                  ? Object.entries(statusData.pipeline_progress.step_details)
                      .filter(([_, detail]) => detail.completed)
                      .map(([step]) => step)
                  : undefined
              }
            />
          )}
        </CardBody>
      </Card>

      {/* Progress Bar (for processing jobs) */}
      {ACTIVE_TTS_JOB_STATUSES.has(effectiveStatus) && (
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">
                {statusData?.current_step || job.status_message || 'Processing...'}
              </span>
              <span className="text-sm text-slate-500">
                {pipelineProgress?.percentage || job.progress || 0}%
              </span>
            </div>
            <Progress value={pipelineProgress?.percentage || job.progress || 0} variant="primary" />
          </CardBody>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
              <DocumentTextIcon className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Events</p>
              <p className="text-xl font-bold text-slate-800">
                {statusData?.events_count || '--'}
              </p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center">
              <SpeakerWaveIcon className="w-6 h-6 text-secondary-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Steps Done</p>
              <p className="text-xl font-bold text-slate-800">
                {pipelineProgress?.completed_steps || 0}/{pipelineProgress?.total_steps || 7}
              </p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-success-100 flex items-center justify-center">
              <ClockIcon className="w-6 h-6 text-success-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Last Step</p>
              <p className="text-xl font-bold text-slate-800 truncate max-w-[100px]" title={statusData?.last_completed_step || '--'}>
                {statusData?.last_completed_step?.replace(/_/g, ' ') || '--'}
              </p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <ArrowDownTrayIcon className="w-6 h-6 text-slate-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Progress</p>
              <p className="text-xl font-bold text-slate-800">
                {pipelineProgress?.percentage || job.progress || 0}%
              </p>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Job Details */}
        <Card className="lg:col-span-1">
          <CardHeader title="Job Details" />
          <CardBody className="space-y-4">
            <div>
              <p className="text-sm text-slate-500">Voice</p>
              <p className="font-medium text-slate-800">{metadata.voice_id || job.voice_id || '--'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">File</p>
              <p className="font-mono text-sm text-slate-600 break-all">{fileName}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Tenant</p>
              <p className="font-medium text-slate-800">{metadata.tenant_id || job.tenant_id || '--'}</p>
            </div>
            {metadata.isbn && (
              <div>
                <p className="text-sm text-slate-500">ISBN</p>
                <p className="font-mono text-sm text-slate-600">{metadata.isbn}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-slate-500">Created</p>
              <p className="font-medium text-slate-800">{formatDate(job.created_at)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Updated</p>
              <p className="font-medium text-slate-800">{formatDate(job.updated_at)}</p>
            </div>
            {job.finished_at && (
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="font-medium text-slate-800">{formatDate(job.finished_at)}</p>
              </div>
            )}
            <div className="pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500 mb-2">Options</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant={metadata.quality === 'high' ? 'primary' : 'slate'}>
                  {metadata.quality || 'standard'} quality
                </Badge>
                {metadata.skip_credits && (
                  <Badge variant="warning">Skip Credits</Badge>
                )}
                {metadata.dry_run && (
                  <Badge variant="secondary">Dry Run</Badge>
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Pipeline Progress */}
        <Card className="lg:col-span-2">
          <CardHeader title="Pipeline Progress" />
          <CardBody>
            <div className="space-y-4">
              {PIPELINE_STEPS.map((step) => {
                const stepData = stepDetails[step.key];
                const status = getStepStatus(stepData);
                const duration = calculateDuration(stepData?.startTime, stepData?.endTime);

                return (
                  <div key={step.key} className="flex items-center gap-4">
                    <div className="w-8 text-center text-xl">{getStepIcon(stepData)}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-slate-800">{step.name}</p>
                        <span className="text-sm text-slate-500">{formatDuration(duration)}</span>
                      </div>
                      <Progress
                        value={status === 'completed' ? 100 : status === 'in_progress' ? 50 : 0}
                        size="sm"
                        variant={status === 'completed' ? 'success' : 'primary'}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Health Status (slow) - gated to avoid false positives */}
      {showSlowProcessingWarning && (
        <Card className="border-amber-200 bg-amber-50">
          <CardBody>
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-800">This is taking longer than usual</p>
                <p className="text-sm text-amber-700">
                  {statusData?.stuck_reason || 'No recent activity detected for this job.'}
                  {lastActivityMinutes !== null && ` (last activity ~${lastActivityMinutes} min ago)`}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Status Message */}
      {job.status_message && (
        <Card>
          <CardHeader title="Current Status" />
          <CardBody>
            <p className="text-slate-700">{job.status_message}</p>
            {statusData?.last_activity && (
              <p className="text-sm text-slate-500 mt-2">
                Last activity: {formatDate(statusData.last_activity)}
              </p>
            )}
          </CardBody>
        </Card>
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}
